import { useEffect, useState } from "react";
import type { Spot } from "../../../shared/goals/placements";
import { Icon } from "../../components/ui/Icon";
import { useNative } from "../../../shared/phone/media";
import { Sheet as PnSheet } from "../../../shared/phone/Sheet";
import { Sheet } from "../../components/ui/Sheet";
import "../../styles/placements.css";

/**
 * Where a goal's ads show (Command Center V2 phase 4, prototype/flashadz-command-center-v2.html). Meta is
 * retiring spot choices on ad sets (notes/channels/zernio-meta-placements.md), so Automatic is the one choice:
 * "Choose the spots" stays in view, off, with the news. A goal that already has spots can go back to Automatic.
 */

/** Meta announced nothing in public; this is the clearest account of the change. */
const RETIRING = "https://www.socialmediatoday.com/news/meta-removes-option-to-exclude-ad-placements/828461/";

/** The pill for where a goal's ads show. */
export const spotsPill = (spots: readonly Spot[] | null | undefined) => (spots?.length ? `${spots.length} spots chosen` : "Meta picks the spots");

interface Props {
  open: boolean;
  onClose: () => void;
  /** The goal, as its heading. */
  title: string;
  ads: number;
  /** The goal's ads are on Meta: a change restarts Meta's learning. */
  onMeta: boolean;
  spots: readonly Spot[] | null | undefined;
  /** Saves the choice; true when it is done and the sheet can close. */
  onSave: (spots: Spot[] | null) => Promise<boolean>;
}

export function PlaceSheet(p: Props) {
  const native = useNative();
  if (native) return <NativePlaces {...p} />;
  return (
    <Sheet open={p.open} onClose={p.onClose} label="Where the ads show" className="place">
      <Body {...p} />
    </Sheet>
  );
}

function Body({ onClose, title, ads, onMeta, spots, onSave }: Props) {
  const had = !!spots?.length;
  const [auto, setAuto] = useState(!had);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const done = await onSave(null);
    setBusy(false);
    if (done) onClose();
  };
  return (
    <>
      <header className="sh-h">
        <span className="eyebrow">Where the ads show</span>
        <h2>{title}</h2>
        <p className="hint">{ads > 1 ? `For all ${ads} ads in this goal.` : "For all the ads in this goal."}</p>
      </header>
      <div className="sh-b" role="radiogroup" aria-label="Where the ads show">
        <button type="button" className={`popt${auto ? " on" : ""}`} role="radio" aria-checked={auto} onClick={() => setAuto(true)}>
          <span className="rd" />
          <span>
            <b>
              Automatic<span className="rec">Recommended</span>
            </b>
            <p>Meta picks the spots and moves the money to where results cost less.</p>
          </span>
        </button>
        <div className={`popt off${auto ? "" : " on"}`} role="radio" aria-checked={!auto} aria-disabled="true">
          <span className="rd" />
          <span>
            <b>Choose the spots</b>
            <p className="retire">
              <Icon name="info" />
              <span>
                Meta is retiring spot choices on ad sets.{" "}
                <a href={RETIRING} target="_blank" rel="noreferrer">
                  Read more
                </a>
              </span>
            </p>
          </span>
        </div>
      </div>
      {had && auto && (
        <footer className="sh-f">
          {onMeta && <p>Meta's learning starts again after this change.</p>}
          <button type="button" className="btn sm ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn sm" disabled={busy} onClick={() => void save()}>
            Save
          </button>
        </footer>
      )}
    </>
  );
}

/** The same choice on a phone and an iPad: a kit sheet with Automatic ticked, and the retired choice shown off. */
function NativePlaces({ open, onClose, title, ads, onMeta, spots, onSave }: Props) {
  const had = !!spots?.length;
  const [auto, setAuto] = useState(!had);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setAuto(!had);
  }, [open, had]);
  const save = async () => {
    setBusy(true);
    const done = await onSave(null);
    setBusy(false);
    if (done) onClose();
  };
  return (
    <PnSheet
      open={open}
      onClose={onClose}
      title="Where the ads show"
      foot={
        had && auto ? (
          <>
            {onMeta && <p className="ad-why">Meta's learning starts again after this change.</p>}
            <button type="button" className="pn-btn pri wide" disabled={busy} onClick={() => void save()}>
              Save
            </button>
          </>
        ) : undefined
      }
    >
      <p className="ad-shs">
        {title}. {ads > 1 ? `For all ${ads} ads in this goal.` : "For all the ads in this goal."}
      </p>
      <div className="pn-grp" role="radiogroup" aria-label="Where the ads show">
        <button type="button" className="pn-row wrap" role="radio" aria-checked={auto} onClick={() => setAuto(true)}>
          <span className="m">
            <span className="t">
              Automatic<span className="pn-pill acc ad-sugg">Recommended</span>
            </span>
            <span className="s">Meta picks the spots and moves the money to where results cost less.</span>
          </span>
          <span className={auto ? "ad-tick" : "ad-tick off"}>
            <Icon name="check" />
          </span>
        </button>
        <div className="pn-row static wrap ad-off" role="radio" aria-checked={!auto} aria-disabled="true">
          <span className="m">
            <span className="t">Choose the spots</span>
            <span className="s">
              Meta is retiring spot choices on ad sets.{" "}
              <a className="ad-acc" href={RETIRING} target="_blank" rel="noreferrer">
                Read more
              </a>
            </span>
          </span>
          <span className={auto ? "ad-tick off" : "ad-tick"}>
            <Icon name="check" />
          </span>
        </div>
      </div>
    </PnSheet>
  );
}
